/* eslint-disable max-lines-per-function, max-lines */
import { EventBus, Events, store } from "jiffy-store";
import { track } from "jiffy-analytics";
import { makeFullPath } from "jiffy-api";

import collectFormItemsQty from "common/utils/form/collect-form-items-qty";
import collectFormDtfSizes from "common/utils/form/collect-form-dtf-sizes";
import mobileApp from "common/utils/mobile/app";
import { observeElementVisibility, isScrollOnTop } from "common/utils/ui/visibility";
import { requestLocalStorage, redirectTo } from "common/utils/browser";
import { incrementHeaderCounter } from "components/jiffy/masthead-cart/masthead-cart";
import idleCallback from "common/utils/ui/idle_callback";
import { refreshCsrfToken } from "common/utils/csrf-token-manager";
import { notifyError } from "common/utils/app-monitoring/notify";
import { productEngagementTracker } from "components/jiffy/product-details/product-engagement-tracker";
import { shouldUseEnhancedUrls, generateEnhancedUrl } from "common/utils/enhanced-url-generator";
import { clearCachedEstimates } from "components/jiffy/product-details/cache-composition-delivery-date";

import { addToCartAnalytics, triggerPdpVisitEvent, trackSizeQuantityMissed } from "./analytics";
import { getPriority } from "./extract-priority";
import productPage from "./product-page";
import updateSellingPrices from "./update-variant-selling-price";
import updateRestockText from "./update-restock-text";
import createPayload from "./show-product-modules/payload";
import validationErrors from "./show-product-modules/validation-errors";
import updateFlashStock from "./update-flash-stock";

const setSubmitButtonLoading = isLoading => {
  const button = document.querySelector(".js-add-to-cart");
  if (!button) return;

  if (isLoading) {
    button.classList.add("product-details-variants-form__submit--loading");
  } else {
    button.classList.remove("product-details-variants-form__submit--loading");
  }
};

const parsePayloadString = payloadStr => {
  // Just convert URL params to a flat object for logging
  // From: "variants[4448][quantity]=1&product_id=1319"
  // To: { "variants[4448][quantity]": "1", "product_id": "1319" }
  const params = new URLSearchParams(payloadStr);
  const result = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

const tokenRefreshHandler = async (context, dataToSubmit, error) => {
  const source = "add_to_cart";
  const success = await refreshCsrfToken(source);
  if (success) {
    setSubmitButtonLoading(true);
    // Keep the original ajax call since it's part of a larger jQuery ajax setup
    $.ajax(context);
  } else {
    track("add_to_cart_error", {
      error: "Token refresh failed",
      code: error.status,
      error_message: error.responseJSON?.error_message || error?.responseText,
      tokenRetries: context.tokenRetryCount,
      networkRetries: context.tryCount,
      payload: parsePayloadString(dataToSubmit)
    });
    redirectTo("/login");
  }
};

const showProduct = {
  setActiveColorName(value) {
    $("#active-color-name").val(value);
    this.productSelectedColor.setActiveColor(value);
  },
  getActiveColorName() {
    return $("#active-color-name").val();
  },
  productInputGrid: {
    onSubmit($form, { dtfParams, madeParams }) {
      mobileApp.mq.publish("feedback", { type: "vibration" });

      const formPath = "/api/cart/add";
      const cartAddURL = makeFullPath(formPath);

      const eventId = document.querySelector(productPage.selectors.formEventId).value;
      const dataToSubmit = createPayload($form, {
        dtfParams,
        madeParams: {
          ...madeParams,
          madePayload: madeParams.madePayload?.filter(p => !p.isEnhancement)
        }
      });
      const isSendingFormData = dataToSubmit instanceof FormData;

      setSubmitButtonLoading(true);

      $.ajax({
        type: "POST",
        url: cartAddURL,
        data: dataToSubmit,
        processData: isSendingFormData ? false : undefined,
        contentType: isSendingFormData ? false : undefined,
        tryCount: 0,
        retryLimit: 3,
        tokenRetryCount: 0,
        tokenRetryLimit: 3,
        retryDelay: 1_000, // 1 second
        beforeSend(xhr) {
          productPage.variantDomStructure.ready = false;
          return xhr.setRequestHeader("X-CSRF-Token", $('meta[name="csrf-token"]').attr("content"));
        },
        error(error, textStatus, errorThrown) {
          if (
            (error.status === 0 && error.readyState === 0 && textStatus !== "abort") ||
            error.status === 502 ||
            error.status === 504
          ) {
            this.tryCount += 1;
            if (this.tryCount <= this.retryLimit) {
              // Retry after some time with exponential back-off
              const delay = this.retryDelay * 2 ** (this.tryCount - 1);
              setTimeout(() => {
                $.ajax(this);
              }, delay);
              return;
            }
          }

          let errorResponse;

          if (error.responseText && error.responseText.trim() !== "") {
            errorResponse = JSON.parse(error.responseText);
          } else {
            errorResponse = {
              error: "Empty response",
              error_message: "Processing Problem. Please refresh the page."
            };
          }

          // Handle token/session expiration
          if (
            error.status === 422 &&
            (errorResponse?.error === "Session expired" ||
              errorResponse?.error === "Empty response")
          ) {
            this.tokenRetryCount += 1;
            if (this.tokenRetryCount <= this.tokenRetryLimit) {
              tokenRefreshHandler(this, dataToSubmit, error);
              return;
            }
          }

          setSubmitButtonLoading(false);

          // If we've exhausted all retries or have a different error
          $("#continue-shopping-modal").modal("hide");
          $("#adding-failure").modal("show");
          $("#adding-failure").find("#adding-failure-error").html(errorResponse?.error_message);

          const payloadData = parsePayloadString(dataToSubmit);
          const errorDetails = Object.fromEntries(
            Object.getOwnPropertyNames(error || {}).map(key => [key, error[key]])
          );

          // Simple diagnostics to determine if it's a network issue
          const diagnostics = {
            onlineStatus: navigator.onLine,
            userAgent: navigator.userAgent,
            connectionType: navigator.connection?.effectiveType,
            isWebView: /WebView|wv/.test(navigator.userAgent),
            likelyCause: navigator.onLine ? "blocking_or_other" : "network_offline"
          };

          notifyError(errorResponse?.error || "Add to cart error", {
            context: {
              tags: "add_to_cart,frontend"
            },
            params: {
              error_message: errorResponse?.error_message,
              status_code: error.status,
              payload: payloadData,
              tokenRetries: this.tokenRetryCount,
              networkRetries: this.tryCount,
              errorDetails,
              diagnostics
            }
          });

          track("add_to_cart_error", {
            error: error.responseText,
            code: error.status,
            payload: payloadData,
            tokenRetries: this.tokenRetryCount,
            networkRetries: this.tryCount,
            errorDetails,
            diagnostics,
            textStatus,
            errorThrown
          });
        },
        success(data) {
          clearCachedEstimates();
          const amount = Object.values(dtfParams.formItemsQty).reduce((a, b) => a + b) || 0;
          incrementHeaderCounter(amount);
          $form.reset();
          productPage.resetEventId();
          productPage.setLineItems(data.line_items);
          productPage.variantDomStructure.ready = true;

          EventBus.publish(Events.ITEMS_ADDED_TO_CART, data);

          const originalCartTotal = parseFloat(data.cart_total);
          store.pdp.change({ originalCartTotal });

          productPage.variantPricesDataLoadPromise().then(() => {
            updateSellingPrices(
              getPriority(originalCartTotal),
              document.getElementById("product-details-quantity-form"),
              { formSubmit: true }
            );
          });

          store.cart.change({ items: data.line_items });
          setSubmitButtonLoading(false);
        }
      });

      EventBus.publish(Events.MOBILE_GRID_SUBMIT, { form: $form, madeParams });
      addToCartAnalytics(
        {
          itemsQty: dtfParams.formItemsQty,
          dtfSizes: dtfParams.formDtfSizes,
          eventId
        },
        false
      );
    },
    publishInputUpdate(input) {
      const { variantId } = input.dataset;
      const form = input.closest("form");
      const urlParams = new URLSearchParams(window.location.search);
      const colorName = urlParams.get("ac");

      let totalValue = 0;
      const variantsQuantity = {};
      Array.from(form.querySelectorAll(".js-quantity-form-input")).forEach(elem => {
        const inputValue = parseInt(elem.value, 10);
        const parsedValue = !Number.isNaN(inputValue) ? inputValue : 0;
        totalValue += parsedValue;

        variantsQuantity[elem.dataset.variantId] = parsedValue;
      });

      EventBus.publish(
        Events.PRODUCT_INPUT_CHANGED,
        form,
        totalValue,
        colorName,
        variantId,
        variantsQuantity
      );
    },
    changeEventProductQuantityInputs() {
      const $inputsWrapper = document.getElementById("js-product-mobile-grid");
      if (!$inputsWrapper || $inputsWrapper.dataset.listeners) return;

      $inputsWrapper.addEventListener("change", event => {
        const input = event.target;
        this.publishInputUpdate(input);
      });

      $inputsWrapper.addEventListener("input", event => {
        const input = event.target;
        input.classList.toggle("has-value", input.value !== "");
        input.setAttribute("data-value", input.value);
      });

      $inputsWrapper.dataset.listeners = true;
    },
    init() {
      const $submitButtons = document.querySelectorAll(".js-add-to-cart");

      for (const $btn of $submitButtons) {
        if ($btn.dataset.enableOnReady === "true") {
          $btn.disabled = false;
        }
      }

      const validation = validationErrors();
      const formSelector = ".js-product-details-quantity-form";
      const $forms = document.querySelectorAll(formSelector);

      if (isScrollOnTop()) {
        observeElementVisibility(
          formSelector,
          (entries, scrolled) => {
            track("viewed_on_pdp", { target: "qty_grid", scrolled });
          },
          { mobileOnly: true, once: true, observerOptions: { threshold: 0.2 } }
        );
      }

      $forms.forEach($form => {
        $form?.addEventListener("submit", event => {
          // Check if this is simplified mode (which includes sample mode)
          const isSimplified = $form.dataset.simplified === "true";

          // For simplified mode, let the form submit normally to design_lab_start_path
          if (isSimplified) {
            // Don't prevent default - let the form submit normally
            return;
          }

          event.preventDefault();
          const madeParams = {
            isMade: false,
            isEdit: false,
            madePayload: null,
            frontInput: $form.elements["field-artwork-upload-front"],
            backInput: $form.elements["field-artwork-upload-back"],
            sample: $form.elements.sample
          };

          const formItemsQty = collectFormItemsQty($($form));
          const formDtfSizes = collectFormDtfSizes($($form));
          const formDtfImage = $form.querySelector(".js-dz-thumbnail")?.src;

          idleCallback(() => {
            EventBus.publish(Events.ADD_TO_CART, {
              items: formItemsQty,
              eventAction: "Quick"
            });
          });

          const hasFormErrors = jQuery.isEmptyObject(formItemsQty);

          if (hasFormErrors) {
            trackSizeQuantityMissed();
          }

          if (hasFormErrors) {
            validation.show({ hasFormErrors });
          } else {
            setSubmitButtonLoading(true);
            const dtfParams = { formItemsQty, formDtfSizes, formDtfImage };
            idleCallback(() => {
              this.onSubmit($form, { dtfParams, madeParams });
            }, 0); // zero is intentional (trying to move to a separate task and then wait for the next idle)
          }
        });
        productPage.variantDomStructure.domLoaded = true;
        productPage.cacheVariantDom();
        this.changeEventProductQuantityInputs();
        EventBus.publish(Events.ORDER_TIER_CHANGED);
      });
    }
  },
  productSelectedColor: {
    setActiveColor(activeColorName, pdpMadeMode = false) {
      const $placeholders = document.querySelectorAll(".js-form-loading-placeholder");
      const jiffyPrint = pdpMadeMode || "";
      const $activePlaceholder = document.querySelector(
        `.js-form-loading-placeholder[data-color-name="${activeColorName}"][data-jiffy-print="${jiffyPrint}"]`
      );

      if ($activePlaceholder) {
        $placeholders.forEach($placeholder => $placeholder.classList.add("hidden"));
        $activePlaceholder.classList.remove("hidden");
        updateFlashStock();
      }
    }
  },
  productVariantColorModal: {
    selectColor(colorData, previousColorGroup) {
      const {
        color: selectedColor,
        colorGroup: newColorGroup,
        showEstimates,
        compositionId,
        softness = "{}",
        supportsMade,
        oos
      } = colorData;

      showProduct.setActiveColorName(selectedColor);

      updateRestockText(selectedColor);

      if (requestLocalStorage()?.getItem("PDPcolorPickerClicked")) {
        requestLocalStorage()?.setItem("compositionReferrer", "PDPColorPicker");
      } else {
        requestLocalStorage()?.setItem("compositionReferrer", "PDPColorModal");
      }

      requestLocalStorage()?.removeItem("PDPcolorPickerClicked");

      triggerPdpVisitEvent(selectedColor);

      // Canonical URL is updated by the color slider's direct method

      // Start engagement tracking with 10-second timer
      const productContainer = document.getElementById("product-page-container");
      const productId = productContainer?.dataset?.productId;
      if (productId && compositionId) {
        productEngagementTracker.trackCompositionChange({
          compositionId,
          compositionName: selectedColor,
          productId
        });
      }

      idleCallback(() => {
        let parsedSoftness;
        try {
          parsedSoftness = JSON.parse(softness);
        } catch {
          parsedSoftness = {};
        }

        EventBus.publish(Events.PRODUCT_COLOR_SELECTED, this, {
          color: selectedColor,
          softness: parsedSoftness,
          showEstimates,
          newColorGroup,
          previousColorGroup,
          compositionId,
          supportsMade: supportsMade === "true",
          oos: oos === "true"
        });
      });
      this.updateDocumentTitle(selectedColor);
      this.updateMetaDescription(selectedColor);
    },

    initColorSelectorHandler() {
      const $colorSelectorToggle = $(".js-color-selector");
      const $modal = $("#products-color-popup");
      const $variantContent = $("#color-variant-selector-content");

      $colorSelectorToggle.on("click", e => {
        e.preventDefault();
        EventBus.publish(Events.PRODUCT_STATIC_COLOR_SELECTED);
      });

      $variantContent.on("click", "a.js-color-variant-selector", event => {
        if (event.currentTarget.pathname !== window.location.pathname) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget;

        const previousColorGroup = $variantContent
          .find("a.js-color-variant-selector.product-color-picker__item--active")
          .data("colorGroup");
        $variantContent
          .find("a.js-color-variant-selector")
          .removeClass("product-color-picker__item--active");

        showProduct.productVariantColorModal.selectColor(target.dataset || {}, previousColorGroup);
        $(target).addClass("product-color-picker__item--active");

        $modal.modal("hide");
        window.history.replaceState(null, null, `${target.pathname}${target.search}`);
      });
    },

    subscribeToColorChangeFromCarousel() {
      EventBus.on(Events.PRODUCT_COLOR_SELECTED_FROM_SLIDER, (evt, { color }) => {
        // Next event loop
        setTimeout(() => {
          const $variantContent = $("#color-variant-selector-content");
          $variantContent
            .find("a.js-color-variant-selector")
            .removeClass("product-color-picker__item--active");
          $variantContent
            .find(`[data-color="${color}"]`)
            .addClass("product-color-picker__item--active");
        });
      });
    },

    subscribeOnPageMode() {
      const $madeWrapper = document.querySelector(".js-product-details-made-wrapper");
      $madeWrapper?.classList.add("empty");
      showProduct.setActiveColorName(showProduct.getActiveColorName());
    },

    updateDocumentTitle(color) {
      const colors = Array.from(document.querySelectorAll(".js-color-variant-selector")).map(
        item => item.dataset.color
      );

      if (colors.length) {
        const regExp = new RegExp(`(${colors.join("|")})`, "g");
        document.title = document.title.replace(regExp, color);
      }
    },
    updateMetaDescription(color) {
      const colors = Array.from(document.querySelectorAll(".js-color-variant-selector")).map(
        item => item.dataset.color
      );

      if (colors.length) {
        const regExp = new RegExp(`(${colors.join("|")})`, "g");
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          const currentContent = metaDescription.getAttribute("content");
          const updatedContent = currentContent.replace(regExp, color);
          metaDescription.setAttribute("content", updatedContent);
        }

        // Update Open Graph description
        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
          const currentContent = ogDescription.getAttribute("content");
          const updatedContent = currentContent.replace(regExp, color);
          ogDescription.setAttribute("content", updatedContent);
        }

        // Update Twitter description
        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescription) {
          const currentContent = twitterDescription.getAttribute("content");
          const updatedContent = currentContent.replace(regExp, color);
          twitterDescription.setAttribute("content", updatedContent);
        }

        // Update meta titles with new color
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
          const currentContent = ogTitle.getAttribute("content");
          const updatedContent = currentContent.replace(regExp, color);
          ogTitle.setAttribute("content", updatedContent);
        }

        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle) {
          const currentContent = twitterTitle.getAttribute("content");
          const updatedContent = currentContent.replace(regExp, color);
          twitterTitle.setAttribute("content", updatedContent);
        }

        // Update URLs for enhanced URL pages
        this.updateMetaUrls(color);
      }
    },

    updateMetaUrls(color) {
      // Only update URLs for enhanced URL pages
      if (shouldUseEnhancedUrls()) {
        // Use the multi-color-aware generator and preserve existing query params
        const enhancedPath = generateEnhancedUrl(color, true);
        const newFullUrl = window.location.origin + enhancedPath;

        // Update all URL-based meta tags
        const urlTags = [
          { selector: "link[rel='canonical']", attr: "href" },
          { selector: "meta[property='og:url']", attr: "content" },
          { selector: "meta[name='twitter:url']", attr: "content" }
        ];

        urlTags.forEach(({ selector, attr }) => {
          const element = document.querySelector(selector);
          if (element) {
            element.setAttribute(attr, newFullUrl);
          }
        });
      }
    },

    init() {
      this.initColorSelectorHandler();
      this.subscribeToColorChangeFromCarousel();
      this.subscribeOnPageMode();
    }
  },
  init() {
    if (!this.getActiveColorName()) {
      return;
    }
    this.productInputGrid.init();
    this.productVariantColorModal.init();
    window.localStorage.removeItem("compositionReferrer");
  },
  changeEventProductQuantityInputs() {
    this.productInputGrid.changeEventProductQuantityInputs();
  }
};

export default showProduct;
